import { useTenantNavigate } from '@app-store'
import {
  Accordion,
  CustomButton,
  Loader,
  PdfViewer,
  SvgCross,
  SvgEdit,
  Tabs,
  TabSplits,
  TabSplitsTypes,
} from '@ui-kit'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import ViewTab from './ViewTab'
import {
  clearCorrespondenceLogsData,
  fetchDownloadUrlForSingleFIles,
  LayoutResponseType,
  LeftPanePDFProps,
  removePDF,
} from '@app-store'
import {
  correspondenceLogsPage,
  ENTITY_NAME,
  NAMESPACES,
} from '../../utils/constants'
import { useParams } from 'react-router-dom'
import { editRoute, entitiesRoute } from '../../routes/route.constant'
import { AgentDeskParams } from '../../types/params.types'
import { TabIndexMap } from '../../types/common'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { getEntityDataForUpload } from '../../apis/fetchPresignedUrl.api'
import { CALL_INTERACTIONS, STRINGS } from '../../utils/strings'
import {
  buildCorrespondenceLogTabs,
  fetchCaseProgramInboundChannelFlags,
  getDefaultActiveTabName,
  openNewWindow,
} from '../../utils/helperFunctions'
import { isReadOnlyUser } from '../../utils/permission'
import { PAGE_LAYOUT } from '../../utils/controlTypes'
import { CorrespondenceLogsTableWrapper } from '../../routes/correspondenceLogs.route'
import InteractionListing from '../../pages/InteractionListing.page'
import { getCaseKeyCaseNameByEntity } from '../../apis/entityData.api'
import IntegrationLogsTable from '../integration-logs/IntegrationLogsTable'
const TabLayout = ({
  layout,
  isWorkflow = false,
  recordId,
  entityId,
  tabIndexMap,
  tabIndexForTabs,
  isPrintLetterView,
}: {
  layout: LayoutResponseType
  isWorkflow?: boolean
  recordId: string
  entityId: string
  tabIndexMap?: TabIndexMap
  tabIndexForTabs?: number
  isPrintLetterView?: boolean
}) => {
  const pdfData = useAppSelector((state: RootState) => state.pdfData)
  const reduxCaseId = useAppSelector(
    (state: RootState) => state.workflowData?.caseDetails?.caseId
  )
  const caseProgramId = useAppSelector(
    (state: RootState) => state.workflowData?.caseData?.programs?.id
  )
  const siteSettings = useAppSelector(
    (state: RootState) => state.authUser.site_settings
  )
  const showInboundSms =
    String(
      siteSettings?.find((s) => s?.key === STRINGS.SHOW_INBOUND_SMS)?.value ??
        ''
    ).toLowerCase() === 'true'
  const [caseId, setCaseId] = useState<string>(reduxCaseId ?? '')
  const dispatch = useAppDispatch()
  const navigate = useTenantNavigate()
  const { id: dataId } = useParams<AgentDeskParams>()
  const [tabs, setTabs] = useState<TabSplitsTypes[]>()
  const [showPdf, setShowPdf] = useState<LeftPanePDFProps>({
    isShow: false,
    url: '',
  })
  const layoutList = useRef<{ [key: string]: LayoutResponseType }>({})
  const [uniqueKey, setUniqueKey] = useState(
    correspondenceLogsPage.OUTBOUND_LOWERCASE
  )
  const [defaultActiveTab, setDefaultActiveTab] = useState<{
    label: string
    component: React.ReactNode
  }>({
    label: getDefaultActiveTabName(uniqueKey),
    component: null,
  })
  const [inboundChannelFlags, setInboundChannelFlags] = useState({
    inboundFaxEnabled: false,
    inboundMailEnabled: false,
  })
  const [inboundChannelFlagsLoaded, setInboundChannelFlagsLoaded] =
    useState(false)

  const tabsList = useMemo(
    () =>
      buildCorrespondenceLogTabs({
        inboundFaxEnabled: inboundChannelFlags.inboundFaxEnabled,
        inboundMailEnabled: inboundChannelFlags.inboundMailEnabled,
        isCaseView: true,
        showInboundSms,
        includeInteraction: true,
      }),
    [inboundChannelFlags, showInboundSms]
  )

  useEffect(() => {
    if (!caseProgramId) {
      setInboundChannelFlags({
        inboundFaxEnabled: false,
        inboundMailEnabled: false,
      })
      setInboundChannelFlagsLoaded(true)
      return
    }

    let isCancelled = false
    setInboundChannelFlagsLoaded(false)

    fetchCaseProgramInboundChannelFlags(caseProgramId).then((flags) => {
      if (isCancelled) {
        return
      }
      setInboundChannelFlags(flags)
      setInboundChannelFlagsLoaded(true)
    })

    return () => {
      isCancelled = true
    }
  }, [caseProgramId])

  useEffect(() => {
    if (!inboundChannelFlagsLoaded) {
      return
    }

    const isFaxKey = uniqueKey === correspondenceLogsPage.INBOUND_LOWERCASE
    const isMailKey =
      uniqueKey === correspondenceLogsPage.INBOUND_MAIL_LOWERCASE

    if (
      (isFaxKey && !inboundChannelFlags.inboundFaxEnabled) ||
      (isMailKey && !inboundChannelFlags.inboundMailEnabled)
    ) {
      setUniqueKey(correspondenceLogsPage.OUTBOUND_LOWERCASE)
      setDefaultActiveTab({
        label: correspondenceLogsPage.OUTBOUND,
        component: null,
      })
    }
  }, [inboundChannelFlags, inboundChannelFlagsLoaded, uniqueKey])

  useEffect(() => {
    const fetchCaseId = async () => {
      try {
        const payload = {
          entity_name: ENTITY_NAME.CASE_LOWER,
          select: [NAMESPACES.CASE_KEY],
        }
        const response = await getCaseKeyCaseNameByEntity(
          payload,
          ENTITY_NAME.CASE_LOWER,
          Number(recordId)
        )
        setCaseId(response?.data?.case_key)
      } catch (error) {
        console.error('Failed to fetch caseId', error)
      }
    }

    if (reduxCaseId) {
      setCaseId(reduxCaseId)
    } else {
      fetchCaseId()
    }
  }, [reduxCaseId, recordId])
  const handleTabChange = (label: string) => {
    setDefaultActiveTab({
      label,
      component: null,
    })
    dispatch(clearCorrespondenceLogsData())
    switch (label) {
      case correspondenceLogsPage.OUTBOUND:
        setUniqueKey(correspondenceLogsPage.OUTBOUND_LOWERCASE)
        break
      case correspondenceLogsPage.INBOUND_FAX:
        setUniqueKey(correspondenceLogsPage.INBOUND_LOWERCASE)
        break
      case correspondenceLogsPage.INBOUND_WEB:
        setUniqueKey(correspondenceLogsPage.INBOUND_WEB_KEY)
        break
      case CALL_INTERACTIONS.INTERACTION:
        setUniqueKey(CALL_INTERACTIONS.INTERACTION_LOWERCASE)
        break
      case correspondenceLogsPage.INBOUND_MAIL:
        setUniqueKey(correspondenceLogsPage.INBOUND_MAIL_LOWERCASE)
        break
    }
  }
  const createTabs = (layout: LayoutResponseType) => {
    const tabsArray: TabSplitsTypes[] = []
    layout?.layout_data?.pages?.forEach((page) => {
      if (page.label === PAGE_LAYOUT.CORRESPONDENCE) {
        tabsArray.push({
          key: page?.pageId,
          label: page?.label,
          tabIndexForTabs: tabIndexForTabs,
          content: (
            <div className="mx-1">
              <div className=" mb-1 border-b-2"></div>
              <div className="py-2">
                <Tabs
                  tabs={tabsList}
                  onChange={handleTabChange}
                  defaultActive={defaultActiveTab}
                  height="1.75rem"
                  width="w-max"
                  tabsWrapperClassName="flex-1"
                  fontWeight="font-normal"
                  bottomColor=""
                  activeFontColor="text-primary-50"
                  inActiveFontColor="text-black"
                  activeBackgroundColor="bg-secondary-500"
                  inActiveBackgroundColor="bg-secondary-50"
                  roundTabs={true}
                />
              </div>
              <Accordion
                heading="Correspondence Details"
                isDefaultActive={true}
              >
                {uniqueKey !== CALL_INTERACTIONS.INTERACTION_LOWERCASE
                  ? caseId && (
                      <CorrespondenceLogsTableWrapper
                        key={uniqueKey}
                        uniqueKey={uniqueKey}
                        caseId={caseId}
                        isCaseView={true}
                      />
                    )
                  : caseId && (
                      <InteractionListing
                        key={uniqueKey}
                        caseId={caseId}
                        isCaseView={true}
                      />
                    )}
              </Accordion>
            </div>
          ),
        })
      } else if (page.label === PAGE_LAYOUT.INTEGRATION_LOGS) {
        tabsArray.push({
          key: page?.pageId,
          label: page?.label,
          tabIndexForTabs: tabIndexForTabs,
          content: <IntegrationLogsTable isCaseTab={true} />,
        })
      } else {
        tabsArray.push({
          key: page?.pageId,
          label: page?.label,
          tabIndexForTabs: tabIndexForTabs,
          content: (
            <ViewTab
              layout={layout}
              page={page}
              layoutList={layoutList}
              recordId={recordId || ''}
              entityId={entityId || ''}
              tabIndexMap={
                page?.pageId ? tabIndexMap?.[page?.pageId] : undefined
              }
              isPrintLetterView={isPrintLetterView}
              isWorkflow={isWorkflow}
            />
          ),
        })
      }
    })
    setTabs(tabsArray)
  }

  // Funciton to get pdf for display in left pane
  const sharePdfDocToRoot = async (docId: string) => {
    if (docId) {
      try {
        const { data } = await getEntityDataForUpload(
          STRINGS.MULTI_UPLOAD_FIELDS_REQUIRED,
          +docId
        )
        const { path, name } = data.attachment[0]

        const { data: pdfurl } = await fetchDownloadUrlForSingleFIles(
          path,
          name
        )
        setShowPdf((prev: LeftPanePDFProps) => {
          return {
            ...prev,
            url: pdfurl,
          }
        })
      } catch (error) {
        console.error(error)
      }
    }
  }

  const hidePdfPane = () => {
    setShowPdf({
      url: '',
      isShow: false,
    })
    dispatch(
      removePDF({
        docId: '',
      })
    )
  }

  function RenderContentArea() {
    let content

    if (!showPdf.isShow) {
      content = <TabSplits tabs={tabs || []} />
    } else if (!showPdf.url) {
      content = (
        <div className="left-pane-pdf-wrap">
          <div className="inset-0 flex items-center justify-center bg-opacity-75 z-[99999] backdrop-bg-opacity-75 bg-accent-0">
            <Loader isGlobal={false} />
          </div>
        </div>
      )
    } else {
      content = (
        <div className="left-pane-pdf-wrap flex-col justify-start">
          <button
            onClick={hidePdfPane}
            className="back absolute top-0 right-0 cursor-pointer z-[2]"
          >
            <SvgCross />
          </button>
          <PdfViewer pdfUrl={showPdf.url} />
        </div>
      )
    }

    return content
  }

  useEffect(() => {
    createTabs(layout)
  }, [layout, tabIndexMap, uniqueKey, caseId, tabsList])

  useEffect(() => {
    if (pdfData.docId) {
      setShowPdf((prev: LeftPanePDFProps) => {
        return {
          ...prev,
          isShow: true,
        }
      })
      sharePdfDocToRoot(pdfData.docId)
    }
  }, [pdfData.docId])

  return (
    <div>
      {isWorkflow ? null : (
        <div className="flex justify-between items-center px-2 pt-2">
          <div className="font-primary">
            <div className="text-base font-medium">{layout?.name}</div>
          </div>
          <div className="flex gap-2">
            {layout?.entity_name === ENTITY_NAME.CASE_LOWER ? (
              <CustomButton
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  openNewWindow(
                    `/entity/${entityId}/workflow/review/${recordId}`
                  )
                }}
                color="primary"
              >
                {NAMESPACES.GO_TO_WORKFLOW}
              </CustomButton>
            ) : null}

            <CustomButton
              onClick={() => {
                navigate(`/${entitiesRoute}/${entityId}`)
              }}
              color="primary"
            >
              {NAMESPACES.BACK_BUTTON}
            </CustomButton>

            {!isReadOnlyUser() ? (
              <CustomButton
                type="button"
                onClick={() => {
                  navigate(
                    `/${entitiesRoute}/${entityId}/${editRoute}/${dataId}`
                  )
                }}
                color="primary"
                isFilled
                className="border flex items-center justify-center"
              >
                <div className="flex items-center justify-center gap-1">
                  <SvgEdit
                    width={'0.75rem'}
                    height={'0.75rem'}
                    className="svg-path-stroke-accent-0"
                  />
                  <span className="text-accent-0 text-xs">
                    {NAMESPACES.EDIT}
                  </span>
                </div>
              </CustomButton>
            ) : null}
          </div>
        </div>
      )}
      <div className="w-full relative">{RenderContentArea()}</div>
    </div>
  )
}

export default TabLayout
